import { costOptimizationTactics } from '../data';

const accentText: Record<string, string> = {
  primary: 'text-primary-600',
  accent: 'text-accent-600',
  secondary: 'text-secondary-600',
  foreground: 'text-foreground-700',
};

const accentDot: Record<string, string> = {
  primary: 'bg-primary-500',
  accent: 'bg-accent-500',
  secondary: 'bg-secondary-500',
  foreground: 'bg-foreground-500',
};

const statementMeta = [
  { volume: '400 trades / month', ledgerLabel: 'Commission ledger' },
  { volume: '100 trades / month', ledgerLabel: 'Entry-cost ledger' },
  { volume: '20 trades / month', ledgerLabel: 'Rollover ledger' },
  { volume: '6 trades / month', ledgerLabel: 'Rollover ledger' },
];

export default function CostOptimizationPlaybook() {
  return (
    <section className="relative w-full bg-background-50 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-background-200 to-transparent"></div>
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-14 md:py-20">
        {/* Header */}
        <div className="mb-10 md:mb-14 max-w-[640px]">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.1] text-foreground-950 tracking-[-0.02em]">
            Your cost-reduction statement.
          </h2>
          <p className="mt-4 text-[15px] text-foreground-500 leading-relaxed">
            Read each strategy like a settlement statement. The dominant cost is what you are
            bleeding every month &mdash; each move below is a credit against it. The figures on the
            right are what you claw back.
          </p>
        </div>

        {/* Statements */}
        <div className="flex flex-col gap-6 md:gap-8">
          {costOptimizationTactics.map((item, idx) => {
            const meta = statementMeta[idx];
            return (
              <article
                key={item.strategyId}
                className="bg-background-50 border border-background-200/80 rounded-sm overflow-hidden"
              >
                {/* Statement header band */}
                <div className="flex flex-col sm:flex-row sm:items-stretch border-b border-dashed border-background-300/70">
                  <div className="flex-1 px-5 md:px-9 py-5 md:py-7">
                    <div className="flex items-center gap-2.5 mb-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${accentDot[item.colorRole]}`}></span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-foreground-400 tabular-nums leading-none">
                        Statement {String(idx + 1).padStart(2, '0')} &middot; {meta.ledgerLabel}
                      </span>
                    </div>
                    <h3 className="text-[24px] md:text-[30px] font-[780] text-foreground-950 tracking-[-0.01em] leading-none">
                      {item.strategyName}
                    </h3>
                    <p className="mt-2.5 text-[13px] text-foreground-500 leading-relaxed max-w-[440px]">
                      {item.subtitle}
                    </p>
                  </div>

                  {/* Dominant cost — mobile: compact horizontal strip; desktop: right column */}
                  <div className="sm:w-[220px] flex-shrink-0 border-t sm:border-t-0 sm:border-l border-dashed border-background-300/70 bg-background-100/50 flex sm:flex-col sm:justify-center px-5 md:px-9 py-3.5 sm:py-7 items-center sm:items-start gap-3 sm:gap-0">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-foreground-400 sm:mb-1.5 whitespace-nowrap">
                      Bleeding on
                    </span>
                    <div className="flex sm:flex-col items-baseline sm:items-start gap-2 sm:gap-0">
                      <span className={`text-[18px] sm:text-[19px] md:text-[21px] font-[750] leading-tight ${accentText[item.colorRole]}`}>
                        {item.dominantCost}
                      </span>
                      <span className="text-[11px] text-foreground-400 tabular-nums sm:mt-1">
                        {meta.volume}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Line items */}
                <div className="px-5 md:px-9 py-4 md:py-6">
                  {item.tactics.map((tactic, ti) => (
                    <div
                      key={ti}
                      className={`py-3.5 md:py-4 ${ti > 0 ? 'border-t border-background-200/60' : ''}`}
                    >
                      {/* Mobile: stacked (title then impact below). Desktop: dotted-leader single row */}
                      <div className="hidden md:flex items-end gap-3">
                        <span className="text-[15px] font-[680] text-foreground-900 leading-snug whitespace-nowrap max-w-[60%] truncate">
                          {tactic.title}
                        </span>
                        <span className="flex-1 border-b border-dotted border-background-300 mb-[5px] min-w-[16px]"></span>
                        <span className={`text-[16px] font-[750] font-mono ${accentText[item.colorRole]} whitespace-nowrap tabular-nums`}>
                          {tactic.impact}
                        </span>
                      </div>
                      {/* Mobile layout */}
                      <div className="flex md:hidden items-start justify-between gap-3">
                        <span className="text-[14px] font-[680] text-foreground-900 leading-snug flex-1">
                          {tactic.title}
                        </span>
                        <span className={`text-[13px] font-[750] font-mono ${accentText[item.colorRole]} whitespace-nowrap tabular-nums flex-shrink-0 mt-[1px]`}>
                          {tactic.impact}
                        </span>
                      </div>
                      <p className="mt-2 text-[13px] text-foreground-500 leading-[1.7] max-w-[600px]">
                        {tactic.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>

        {/* Footnote — statement disclaimer style */}
        <p className="mt-10 text-[12px] text-foreground-400 leading-relaxed max-w-[640px] italic">
          Figures are modeled on the account assumptions used throughout this page. Your actual
          savings depend on broker terms, traded volume, and holding periods.
        </p>
      </div>
    </section>
  );
}